import { useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";

import Thumbtack from "@/assets/icons/thumbtack.svg?react";
import { useMediaQuery } from "@/hooks";
import { useHomeSummary } from "@/modules/home";
import { StateMessage } from "@/screens/control-panel/components/state-message";
import { ObrasView } from "./components/obras-view";
import { OverviewView } from "./components/overview-view";
import {
  HOME_DEFAULT_VIEW,
  HOME_NARROW_QUERY,
  HOME_VIEWS,
  type HomeViewId,
} from "./home.constants";
import styles from "./home-screen.module.scss";

/**
 * Tela Inicial do Hub (US-1614): porta de entrada do sistema, com a Visão Geral
 * do portfólio e as Obras em Andamento sobre o painel escuro do protótipo.
 *
 * Uma única consulta alimenta as duas visões, de modo que alternar entre elas é
 * apenas troca de apresentação. A seção de notificações do protótipo não faz
 * parte desta entrega (AC9) e os itens de menu lateral seguem os já existentes
 * (AC8), por isso a tela não os declara.
 */
export function HomeScreen() {
  const { status, summary, errorMessage, reload } = useHomeSummary();

  // AC10 — o protótipo responsivo mantém as duas visões e troca a barra de abas
  // pela lista suspensa; só a forma do seletor muda com a largura.
  const isNarrow = useMediaQuery(HOME_NARROW_QUERY);
  const [activeView, setActiveView] = useState<HomeViewId>(HOME_DEFAULT_VIEW);

  const activeLabel = HOME_VIEWS.find((view) => view.id === activeView)?.label ?? "Tela Inicial";

  // `data-full-bleed` avisa o `page-wrapper` que, no responsivo, esta tela
  // dispensa o recuo padrão da área de conteúdo: o painel do protótipo vai de
  // borda a borda.
  return (
    <section className={styles.screen} data-full-bleed="mobile" data-testid="hub-web-home">
      {/* Cada visão traz a sua fotografia de fundo, como no protótipo. */}
      <div
        className={[
          styles.panel,
          activeView === "visao-geral" ? styles.panelVisaoGeral : styles.panelObras,
        ].join(" ")}
      >
        <header className={styles.header}>
          <h1 className={styles.title}>Início</h1>

          {isNarrow ? (
            <Dropdown
              className={styles.select}
              panelClassName={styles.selectPanel}
              value={activeView}
              options={HOME_VIEWS.map((view) => ({ label: view.label, value: view.id }))}
              onChange={(event) => setActiveView(event.value as HomeViewId)}
              aria-label="Visões da Tela Inicial"
              data-testid="home-view-select"
            />
          ) : (
            <div className={styles.tabs} role="tablist" aria-label="Visões da Tela Inicial">
              {HOME_VIEWS.map((view) => {
                const isActive = view.id === activeView;

                return (
                  <button
                    key={view.id}
                    type="button"
                    role="tab"
                    id={`${view.testId}-tab`}
                    className={[styles.tab, isActive ? styles.tabActive : ""]
                      .filter(Boolean)
                      .join(" ")}
                    aria-selected={isActive}
                    aria-controls="home-view-panel"
                    onClick={() => setActiveView(view.id)}
                    data-testid={view.testId}
                  >
                    {view.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* O protótipo Desktop fecha o topo do painel com o botão de fixar a
              visão; o responsivo não o traz. A ação em si depende das
              preferências do usuário, que ainda não existem no Hub, por isso
              aparece desabilitado em vez de prometer um efeito que não há. */}
          {isNarrow ? null : (
            <button
              type="button"
              className={styles.pin}
              disabled
              title="Disponível quando as preferências da Tela Inicial forem liberadas."
              aria-label="Fixar visão"
              data-testid="home-pin"
            >
              <Thumbtack className={styles.pinIcon} aria-hidden />
            </button>
          )}
        </header>

        <div
          className={[
            styles.viewPanel,
            activeView === "visao-geral" ? styles.viewPanelOverview : "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="tabpanel"
          id="home-view-panel"
          aria-label={activeLabel}
        >
          {status === "loading" ? (
            <StateMessage
              icon="pi pi-spin pi-spinner"
              title="Carregando as informações da Tela Inicial..."
              testId="home-loading"
            />
          ) : null}

          {status === "error" ? (
            <StateMessage
              icon="pi pi-exclamation-triangle"
              title="Não foi possível carregar as informações da Tela Inicial."
              description={errorMessage ?? undefined}
              testId="home-error"
            >
              <Button
                type="button"
                label="Tentar novamente"
                icon="pi pi-refresh"
                onClick={reload}
                data-testid="home-retry"
              />
            </StateMessage>
          ) : null}

          {status === "success" && summary ? (
            activeView === "visao-geral" ? (
              <OverviewView overview={summary.overview} />
            ) : (
              <ObrasView obrasEmAndamento={summary.obrasEmAndamento} />
            )
          ) : null}
        </div>
      </div>
    </section>
  );
}
